/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import React, { useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  IconButton,
  Popover,
  Box,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { selectInterpreterOptions } from 'modules/dashboard/selectors';
import { dashboardActions } from 'modules/dashboard/slice';
import { EXPORT_OPTIONS, MODEL_OPTIONS, OUTPUT_OPTIONS } from 'modules/dashboard/constants';
import TuneIcon from '@mui/icons-material/Tune';

const InterpreterAgentOptionsSelector = ({
  isCerulean,
  isExcelExportEnabled,
  exportType,
  setExportType,
  isRibbonViewEnabled,
}) => {
  const dispatch = useDispatch();
  const interpreterOptions = useSelector(selectInterpreterOptions);
  const [anchorEl, setAnchorEl] = useState(null);
  //
  const open = Boolean(anchorEl);
  const id = open ? 'dropdown-popover' : undefined;
  //
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === 'exportType') {
      setExportType(value);
    }
    dispatch(dashboardActions.setInterpreterOptions({ name, value }));
  };

  const getOutputOption = () => {
    if (isRibbonViewEnabled) return OUTPUT_OPTIONS.RIBBON_VIEW;
    if (isCerulean) return OUTPUT_OPTIONS.CERULEAN;
    return OUTPUT_OPTIONS.PBI;
  };

  const options = getOutputOption();

  const availableOptions = isExcelExportEnabled
    ? options
    : options.filter((option) => option !== EXPORT_OPTIONS.EXCEL_EXPORT);
  //
  return (
    <>
      <IconButton
        aria-describedby={id}
        onClick={handleClick}
        sx={{
          backgroundColor: open ? '#dbeefd' : '#f0f4f9',
          borderRadius: 2,
          p: 1,
          '&:hover': {
            backgroundColor: '#d0e3ff',
          },
        }}
      >
        <TuneIcon sx={{ color: '#1a1b1f' }} />
      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 150,
            mt: 1,
            p: 2,
            borderRadius: 3,
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          },
        }}
      >
        <Box>
          <Stack direction="column" spacing={2} alignItems="center" justifyContent="space-between">
            {/* Model Dropdown */}
            <FormControl
              size="small"
              fullWidth
              sx={{
                '& .MuiInputLabel-root': {
                  fontSize: '0.75rem',
                  lineHeight: '1rem',
                },
                '& .MuiInputLabel-shrink': {
                  transform: 'translate(14px, -6px) scale(0.75)',
                },
              }}
            >
              <InputLabel>Model</InputLabel>
              <Select
                fullWidth
                size="small"
                name="modelName"
                value={interpreterOptions.modelName}
                label="Model"
                onChange={handleChange}
                sx={{
                  fontSize: '0.75rem',
                  height: '28px',
                  '& .MuiOutlinedInput-input': {
                    padding: '6px 6px',
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '& .MuiSelect-select.MuiSelect-outlined': {
                    minHeight: '1rem',
                    lineHeight: '1rem',
                    paddingY: '1px',
                  },
                }}
              >
                {MODEL_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.75rem' }}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Output Dropdown */}
            <FormControl
              size="small"
              fullWidth
              sx={{
                '& .MuiInputLabel-root': {
                  fontSize: '0.75rem',
                  lineHeight: '1rem',
                },
                '& .MuiInputLabel-shrink': {
                  transform: 'translate(14px, -6px) scale(0.75)',
                },
              }}
            >
              <InputLabel>Export Type</InputLabel>
              <Select
                fullWidth
                size="small"
                name="exportType"
                value={exportType}
                label="Export Type"
                onChange={handleChange}
                sx={{
                  fontSize: '0.75rem',
                  height: '28px',
                  '& .MuiOutlinedInput-input': {
                    padding: '6px 6px',
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '& .MuiSelect-select.MuiSelect-outlined': {
                    minHeight: '1rem',
                    lineHeight: '1rem',
                    paddingY: '1px',
                  },
                }}
              >
                {availableOptions.map((option) => (
                  <MenuItem key={option} value={option} sx={{ fontSize: '0.75rem' }}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};
//
export default InterpreterAgentOptionsSelector;
